import CloseIcon from "@mui/icons-material/Close";
import { ChevronRightRounded, DescriptionOutlined } from "@mui/icons-material";
import {
    Box,
    Button,
    ButtonBase,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import { useRevalidator } from "react-router-dom";

import { isRealEnvelopeId } from "../envelope-id";
import { useCurrentRoomUser } from "../hooks/useCurrentRoomUser";
import { isStaff } from "../hooks/useVisiblePages";
import { roomSigner, signerCandidates } from "../recipient-domains";
import { colors, palette, radius, semantic } from "../theme/tokens";

import { PdfScroll } from "./PdfScroll";
import { SignInRoom } from "./SignInRoom";
import { findOpenTerms } from "./use-pdf-document";
import type { WidgetProps } from "./types";

// The Agreements page: a document list on the left, a reading pane on the
// right (Bill, 2026-09-07; it was a row of tiles with iframe previews before).
//
// Each row carries one agreement through its whole life, and only the pane
// changes as it moves:
//   - In preparation: no draft yet; the pane says so.
//   - Draft: the pane scrolls through the draft PDF (PdfScroll).
//   - Out for signature: "Review and sign here" for a signer still to sign,
//     which opens DocuSign's focused view in the pane (SignInRoom,
//     2026-10-14); the DocuSign email still works.
//   - Signed: the signed copy in the viewer (`signedUrl` once pulled into the
//     Agreements folder, DocuSign's completed document until then).
//   - Declined / voided: the draft as sent.
// No DocuSign status card above the document (Elijah, 2026-10-28): the
// card's status pill already says Signed, Declined or Voided.
// The swap from draft to signature faces is `isRealEnvelopeId()`; a
// provisioner placeholder id keeps the draft face.
//
// The first row is selected on load, so the page reads as a document from
// the first second. The pane is as tall as the viewport (the Contracts
// template renders BasePage with `fillViewport`) and scrolls inside itself.
// On a phone the list shows alone and a tap opens the pane in a full-screen
// dialog. Each card ends with a status pill (draft is yellow).

type Item = WidgetProps<"agreements">["widget"]["props"]["items"][number];

// One plain sentence per canonical agreement, used when the room's item has
// no description of its own (rooms converted from the legacy shape).
const AGREEMENT_LINES: Record<string, string> = {
    "seller-of-record":
        "Appoints Bison as seller of record so we can list and sell your products on the marketplaces we cover.",
    "supplier-program-agreement":
        "Sets the commercial terms: payment terms and the marketing and damages allowances.",
    "white-label":
        "Covers the products Bison sells under its own brands, and what stays yours.",
    exclusivity:
        "Makes Bison the exclusive authorized seller on Amazon and grants the rights to run listings, storefront, A+ and advertising.",
};

// The selected card's border and chevron (Bill, 2026-10-14).
const SELECTED = "#001F38";

type Face = "preparing" | "draft" | "out" | "signed" | "declined" | "voided";

function faceOf(item: Item): Face {
    if (!isRealEnvelopeId(item.envelopeId)) return item.draftUrl ? "draft" : "preparing";
    switch (item.status) {
        case "completed":
            return "signed";
        case "declined":
            return "declined";
        case "voided":
        case "deleted":
            return "voided";
        default:
            return "out";
    }
}

// The status pill at the bottom of each card (Bill, 2026-09-07): a draft is
// yellow, in preparation gray, out for signature blue, signed green, declined
// and voided on the danger tint.
function statusPill(item: Item): { text: string; bg: string; border: string; color: string } {
    switch (faceOf(item)) {
        case "preparing":
            return { text: "In preparation", bg: colors.lightGray, border: colors.line, color: colors.darkGray };
        case "draft":
            return { text: "Draft", bg: palette.warningBg, border: palette.warningBorder, color: semantic.warning.text };
        case "out":
            return { text: "Out for signature", bg: palette.infoBg, border: palette.infoBorder, color: palette.blueDark };
        case "signed": {
            const at = item.envelope?.signedAt;
            const d = at ? new Date(at) : null;
            const when =
                d && !isNaN(d.getTime())
                    ? ` ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
                    : "";
            return { text: `Signed${when}`, bg: palette.successBg, border: palette.successBorder, color: palette.green };
        }
        case "declined":
            return { text: "Signing declined", bg: semantic.danger.bg, border: semantic.danger.border, color: semantic.danger.text };
        case "voided":
            return { text: "Voided", bg: semantic.danger.bg, border: semantic.danger.border, color: semantic.danger.text };
    }
}

export function Agreements({ widget, room }: WidgetProps<"agreements">) {
    const { items } = widget.props;
    const theme = useTheme();
    const phone = useMediaQuery(theme.breakpoints.down("md"));
    const [selectedId, setSelectedId] = useState(items[0]?.id);
    const [open, setOpen] = useState(false);
    const selected = items.find((i) => i.id === selectedId) ?? items[0];
    const { user } = useCurrentRoomUser();
    // Staff only, and only on a draft: once a real envelope id lands the
    // pane leaves the draft face, so the button goes with it.
    const canSend = isStaff(user?.email);

    // One card per document, each ending in a chevron that points at the pane
    // (Bill, 2026-10-14, option 2 of ten mockups, after joined tabs and a single
    // bordered list were tried and dropped the same day; every room since then).
    // A red two-digit number before the title, the status pill top right beside
    // it, the description under. Hover tints the card; the selected card has a
    // #001F38 border and chevron over a soft blue halo. No ripple. On a phone
    // nothing is selected and a tap opens the pane in a dialog.
    const list = (
        <Stack spacing={2} sx={{ alignSelf: "flex-start", width: "100%" }} role="listbox" aria-label="Agreements">
            {items.map((item, i) => {
                const active = !phone && item.id === selected?.id;
                const pill = statusPill(item);
                return (
                    <ButtonBase
                        key={item.id}
                        onClick={() => {
                            setSelectedId(item.id);
                            if (phone) setOpen(true);
                        }}
                        role="option"
                        aria-selected={active}
                        disableRipple
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            width: "100%",
                            textAlign: "left",
                            px: 2.5,
                            py: 2,
                            bgcolor: "#FFFFFF",
                            border: `1px solid ${active ? SELECTED : colors.line}`,
                            borderRadius: `${radius.lg}px`,
                            boxShadow: active ? `0 0 0 3px ${palette.infoBg}` : "none",
                            "&:hover": { bgcolor: active ? "#FFFFFF" : "#FAFAFC" },
                            transition: "background-color 120ms, border-color 120ms, box-shadow 120ms",
                            "&:focus-visible": { outline: `2px solid ${colors.navy}`, outlineOffset: 2 },
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <Typography sx={{ fontWeight: 700, color: colors.navy, flex: 1, minWidth: 0 }}>
                                    <Box component="span" sx={{ color: colors.brandOrange, mr: 1 }}>
                                        {String(i + 1).padStart(2, "0")}
                                    </Box>
                                    {item.title}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={pill.text}
                                    sx={{
                                        height: 22,
                                        flexShrink: 0,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        borderRadius: `${radius.sm}px`,
                                        bgcolor: pill.bg,
                                        color: pill.color,
                                        border: `1px solid ${pill.border}`,
                                    }}
                                />
                            </Stack>
                            <Typography variant="body2" sx={{ color: colors.darkGray, mt: 0.5 }}>
                                {item.description ?? AGREEMENT_LINES[item.id]}
                            </Typography>
                        </Box>
                        <ChevronRightRounded sx={{ flexShrink: 0, color: active ? SELECTED : colors.mediumGray }} />
                    </ButtonBase>
                );
            })}
        </Stack>
    );

    if (phone) {
        return (
            <>
                {list}
                <Dialog open={open} fullScreen onClose={() => setOpen(false)}>
                    {selected && (
                        <Pane
                            item={selected}
                            room={room}
                            canSend={canSend}
                            onClose={() => setOpen(false)}
                        />
                    )}
                </Dialog>
            </>
        );
    }

    return (
        <Box
            sx={{
                flex: 1,
                minHeight: 0,
                display: "grid",
                gridTemplateColumns: "minmax(260px, 1fr) 2fr",
                gap: 3,
                alignItems: "stretch",
            }}
        >
            {list}
            <Box sx={{ minHeight: 0, minWidth: 0 }}>
                {selected && <Pane item={selected} room={room} canSend={canSend} />}
            </Box>
        </Box>
    );
}

// The reading pane for one agreement. With `onClose` it is inside the phone's
// full-screen dialog: the draft viewer takes the whole dialog (its own bar
// carries the close button), the other faces get a header row with the
// title and the close button above the document.
function Pane({
    item,
    room,
    onClose,
    canSend,
}: {
    item: Item;
    room: WidgetProps<"agreements">["room"];
    onClose?: () => void;
    canSend?: boolean;
}) {
    const face = faceOf(item);

    if (face === "draft" && item.draftUrl) {
        const send = canSend && <SendForSignature item={item} room={room} />;
        return <PdfScroll key={item.id} url={item.draftUrl} title={item.title} onClose={onClose} action={send} />;
    }

    const header = onClose && (
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ fontWeight: 700, color: colors.navy, flex: 1 }} noWrap>
                {item.title}
            </Typography>
            <IconButton onClick={onClose} aria-label="Close" size="small">
                <CloseIcon />
            </IconButton>
        </Stack>
    );

    if (face === "preparing") {
        return (
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                {header}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 240,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 1.5,
                        p: 4,
                        border: 1,
                        borderColor: colors.line,
                        borderRadius: `${radius.lg}px`,
                        bgcolor: colors.lightGray,
                        color: colors.mediumGray,
                    }}
                >
                    <DescriptionOutlined sx={{ fontSize: 40 }} />
                    <Typography variant="body2" sx={{ maxWidth: 420 }}>
                        Your Bison contact is preparing this document. It will appear here for
                        review before anything goes out for signature.
                    </Typography>
                </Box>
            </Box>
        );
    }

    // Out for signature (Bill, 2026-10-14): no status card; SignInRoom shows
    // everyone the document as DocuSign holds it now, signatures and all, and
    // a signer still to sign the "Review and sign here" button above it.
    // Signed, declined, voided: the document alone, read-only, for everyone
    // (no status card since 2026-10-28, Elijah): the signed copy once it is
    // filed (DocuSign's own completed document until then), the draft as sent
    // on a declined or voided one.
    const readUrl =
        face === "signed"
            ? (item.signedUrl ?? `/api/rooms/${room.slug}/agreements/${encodeURIComponent(item.id)}/document`)
            : item.draftUrl;
    const reader = readUrl && (
        <PdfScroll
            key={`${item.id}-${readUrl}`}
            url={readUrl}
            title={face === "signed" ? `${item.title} (signed)` : item.title}
        />
    );
    const pad = { px: onClose ? 2 : 0, pb: onClose ? 2 : 0 };

    return (
        <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", gap: 3 }}>
            {header}
            {face === "out" ? (
                // Out for signature: no status block; the document as DocuSign
                // holds it now, signatures and all (Bill, 2026-10-14).
                <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", ...pad }}>
                    <SignInRoom key={item.id} slug={room.slug} itemId={item.id} title={item.title} />
                </Box>
            ) : (
                reader && <Box sx={{ flex: 1, minHeight: 360, ...pad }}>{reader}</Box>
            )}
        </Box>
    );
}

// Staff-only "Send for signature" on a draft, behind a confirmation so a
// stray click never sends anything (2026-10-18; every room since the Behold Home trial). The
// signer is one of the room's named recipients; William Cronin countersigns
// (POST /api/rooms/{slug}/agreements/{id}/send). The message is the DocuSign
// email body, editable before sending. Opening the dialog reads the draft for
// open `[[TERM: ...]]` placeholders (the red terms) and holds the send until
// the draft has none (Bill, 2026-10-25); if the read fails it warns and lets
// staff decide.
function SendForSignature({ item, room }: { item: Item; room: WidgetProps<"agreements">["room"] }) {
    const revalidator = useRevalidator();
    const signers = signerCandidates(room.recipients);
    const [confirming, setConfirming] = useState(false);
    // Defaults to the room's signer, picked in the staff access panel.
    const [signer, setSigner] = useState(roomSigner(room.recipients, room.signerEmail)?.email ?? "");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // null while the draft is being read; "unchecked" when the read failed.
    const [openTerms, setOpenTerms] = useState<string[] | "unchecked" | null>(null);
    const chosen = signers.find((r) => r.email === signer);
    const termsBlock = openTerms === null || (Array.isArray(openTerms) && openTerms.length > 0);
    const defaultMessage = (name?: string) => `Hi ${name?.split(" ")[0] ?? "there"}, please review and sign the ${item.title}.`;

    const open = () => {
        setMessage(defaultMessage(chosen?.name));
        setError(null);
        setOpenTerms(null);
        setConfirming(true);
        if (!item.draftUrl) return setOpenTerms("unchecked");
        findOpenTerms(item.draftUrl).then(setOpenTerms, () => setOpenTerms("unchecked"));
    };
    const send = async () => {
        setSending(true);
        setError(null);
        try {
            const res = await fetch(`/api/rooms/${room.slug}/agreements/${encodeURIComponent(item.id)}/send`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ signerEmail: chosen?.email, signerName: chosen?.name, message }),
            });
            const body = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
            if (!res.ok) throw new Error(body.detail ?? body.error ?? `Send failed (${res.status})`);
            setConfirming(false);
            revalidator.revalidate();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Send failed");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Button variant="contained" color="secondary" size="small" sx={{ flexShrink: 0 }} onClick={open}>
                Send for signature
            </Button>
            <Dialog open={confirming} onClose={() => !sending && setConfirming(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontSize: "1.1rem" }}>Send for signature?</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This sends the {item.title} to {room.supplier.supplierName} through DocuSign. William Cronin
                        countersigns after them. Once it is sent, the draft can no longer be changed in this room.
                    </DialogContentText>
                    {signers.length === 0 ? (
                        <Typography variant="body2" sx={{ color: semantic.danger.text }}>
                            This room has no named recipient to sign. Add the signer to the room first.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            <TextField
                                select
                                size="small"
                                label="Signer"
                                value={signer}
                                onChange={(e) => {
                                    setSigner(e.target.value);
                                    setMessage(defaultMessage(signers.find((r) => r.email === e.target.value)?.name));
                                }}
                            >
                                {signers.map((r) => (
                                    <MenuItem key={r.email} value={r.email}>
                                        {r.name ? `${r.name} (${r.email})` : r.email}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField label="Message" multiline minRows={3} value={message} onChange={(e) => setMessage(e.target.value)} />
                        </Stack>
                    )}
                    {openTerms === null && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
                            Checking the draft for open terms...
                        </Typography>
                    )}
                    {Array.isArray(openTerms) && openTerms.length > 0 && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: "8px", bgcolor: semantic.danger.bg, color: semantic.danger.text }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                The draft still has {openTerms.length} open {openTerms.length === 1 ? "term" : "terms"} in red. Fill{" "}
                                {openTerms.length === 1 ? "it" : "them"} in and re-draft the agreement before sending:
                            </Typography>
                            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                                {openTerms.map((t) => (
                                    <Typography component="li" variant="body2" key={t}>
                                        {t}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {openTerms === "unchecked" && (
                        <Typography variant="body2" sx={{ color: semantic.danger.text, mt: 2 }}>
                            The draft could not be checked for open terms. Read it for any red placeholders before sending.
                        </Typography>
                    )}
                    {error && (
                        <Typography variant="body2" sx={{ color: semantic.danger.text, mt: 2 }}>
                            {error}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirming(false)} disabled={sending} sx={{ textTransform: "none" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={sending || !chosen?.name || termsBlock}
                        onClick={() => void send()}
                        sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                        {sending ? "Sending..." : "Send for signature"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
